import { DOCUMENT } from '@angular/common';
import {
  APP_INITIALIZER,
  EnvironmentProviders,
  Provider,
  inject,
} from '@angular/core';

import { environment, EnvironmentName } from '@ts/shared/util-environment';

const STAGING_COLOR = '#dc2626';
const DEVELOPMENT_COLOR = '#2563eb';

const ENVIRONMENT_COLOR: Readonly<
  Record<Exclude<EnvironmentName, 'production'>, string>
> = {
  staging: STAGING_COLOR,
  development: DEVELOPMENT_COLOR,
};

const BORDER_CLASS = '_GLOBAL-environment-indicator-border';
const RIBBON_CLASS = '_GLOBAL-environment-indicator-ribbon';
const STYLE_ID = '_GLOBAL-environment-indicator-style';

/**
 * Provider that injects a fixed-position visual indicator (colored viewport
 * border + diagonal corner ribbon) and swaps the favicon when running in a
 * non-production environment.
 *
 * Goal: prevent staff from accidentally treating staging as the real site.
 *
 * The indicator is overlay-only (`position: fixed`, `pointer-events: none`),
 * so it does not affect the layout, focus, or interactivity of the underlying
 * app — preserving testing fidelity with production.
 *
 * No-op in production.
 */
export function provideEnvironmentIndicator(): readonly (
  | Provider
  | EnvironmentProviders
)[] {
  if (environment.environmentName === 'production') {
    return [];
  }
  const environmentName = environment.environmentName as Exclude<
    EnvironmentName,
    'production'
  >;
  return [
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const document = inject(DOCUMENT);
        return () => renderEnvironmentIndicator(document, environmentName);
      },
    },
  ];
}

export function renderEnvironmentIndicator(
  document: Document,
  environmentName: Exclude<EnvironmentName, 'production'>,
): void {
  const label = environmentName.toUpperCase();
  const color = ENVIRONMENT_COLOR[environmentName];

  injectStyle(document, color);
  injectBorderAndRibbon(document, label);
  swapFavicon(document, color, label.charAt(0));
}

function injectStyle(document: Document, color: string): void {
  if (document.getElementById(STYLE_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .${BORDER_CLASS} {
      position: fixed;
      inset: 0;
      border: 6px solid ${color};
      pointer-events: none;
      z-index: 2147483646;
    }
    .${RIBBON_CLASS} {
      position: fixed;
      right: -60px;
      bottom: 20px;
      width: 200px;
      height: 30px;
      line-height: 30px;
      transform: rotate(-45deg);
      background: ${color};
      color: white;
      text-align: center;
      font-family: Arial, sans-serif;
      font-weight: 900;
      font-size: 14px;
      letter-spacing: 2px;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
      pointer-events: none;
      z-index: 2147483647;
    }
  `;
  document.head.appendChild(style);
}

function injectBorderAndRibbon(document: Document, label: string): void {
  const body = document.body;
  if (!body) {
    return;
  }
  if (!body.querySelector(`.${BORDER_CLASS}`)) {
    const border = document.createElement('div');
    border.className = BORDER_CLASS;
    body.appendChild(border);
  }
  if (!body.querySelector(`.${RIBBON_CLASS}`)) {
    const ribbon = document.createElement('div');
    ribbon.className = RIBBON_CLASS;
    ribbon.textContent = label;
    body.appendChild(ribbon);
  }
}

function swapFavicon(
  document: Document,
  color: string,
  badgeChar: string,
): void {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'><rect width='64' height='64' rx='12' fill='${color}'/><text x='32' y='44' font-family='Arial,sans-serif' font-size='40' font-weight='900' fill='white' text-anchor='middle'>${badgeChar}</text></svg>`;
  const href = `data:image/svg+xml,${encodeURIComponent(svg)}`;

  const existingLinks =
    document.head.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]');
  existingLinks.forEach((link) => link.remove());

  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/svg+xml';
  link.href = href;
  document.head.appendChild(link);
}
